/**
 * 模組頁面代碼 : 1210-UI-01
 * 頁面說明 : 案件資料清單列表頁面
 * @module modules/case/components/CasesPage
 */

// ----- import from npm modules -----
import React, {useRef} from 'react';

// ----- import from base libraries -----

import {useEffect, useState} from 'react';

import enumList from 'web/utils/enumList';
import {BaseComponent} from 'web/components/BaseComponent';
import useListActions from '../hooks/useList';
import DataTableComponent from 'web/components/DataTableComponent';
import EnableSwitch from 'web/components/EnableSwitch';

import UpdateButton from 'web/components/UpdateButton';
import {useNavigate} from 'react-router';

const ListPage = (props) => {
  const navigate = useNavigate();
  const baseComponent = new BaseComponent(props);
  const [searchContent, realSetSearchContent] = useState({
    code: undefined,
    name: undefined,
    flgStatus: undefined,
  });
  const [appearances, setAppearances] = useState([]);
  const {fetchAppearances, updateAppearanceFlgStatus} = useListActions();
  const tableRef = useRef(null);
  const columnConfigs = [
    {title: '現象編號', className: 'dt-center', data: 'code'},
    {title: '現象名稱', className: 'dt-center', data: 'name'},
    {title: '現象說明', className: 'dt-center', data: 'description'},
    {
      orderable: false,
      order: null,
      title: '現象狀態',
      data: null,
      className: 'dt-center',
    },
    {orderable: false, order: null, data: 'id', className: 'dt-right'},
  ];

  const dataTableConfigs = {
    fixedColumns: {
      start: 1,
    },
    paging: true,
    scrollX: true,
  };

  const slotConfigs = {
    3: ({id, flgStatus}) => (
      <EnableSwitch
        disabled={
          !baseComponent.checkPermission(
            enumList.moduleEnums.appearance,
            enumList.accessModeEnums.update,
          )
        }
        flgStatus={flgStatus}
        onChange={async (e) => {
          await updateAppearanceFlgStatus({id, flgStatus: e.nextValue});
        }}
      ></EnableSwitch>
    ),
    4: (data) => (
      <UpdateButton
        navigate={navigate}
        id={data}
        baseURL="/module/appearances"
        hasUpdatePermission={baseComponent.checkPermission(
          enumList.moduleEnums.appearance,
          enumList.accessModeEnums.update,
        )}
        showDetailWhileNoUpdatePermission={true}
      ></UpdateButton>
    ),
  };

  const setSearchContent = (options) =>
    realSetSearchContent({
      ...searchContent,
      ...options,
    });

  const executeSearchData = async () => {
    tableRef.current?.dt().processing(true);
    const data = await fetchAppearances({filters: searchContent});
    setAppearances(data);
    tableRef.current?.dt().processing(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    executeSearchData();
  };

  const handleCreateClick = () => {
    navigate('/module/appearances-apply');
  };

  useEffect(() => {
    baseComponent.checkPermission(
      enumList.moduleEnums.appearance,
      enumList.accessModeEnums.read,
    );
    executeSearchData();
    return () => {
      setAppearances([]);
    };
  }, []);

  return (
    <section id="datatable-container">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <svg
              className="nav__icon nav__icon--breadcrumb me-3"
              aria-hidden="true"
            >
              <use href="#ico-home-line"></use>
            </svg>
            AI智能派工
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            現象管理
          </li>
        </ol>
      </nav>
      <h4>現象管理</h4>
      <div className="datatable__search">
        <form
          className="d-flex gap-2 align-items-end needs-validation"
          noValidate
          onSubmit={handleSearch}
        >
          <div className="flex-fill w-100">
            <label htmlFor="appearance-search-code" className="form-label">
              現象編號
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              id="appearance-search-code"
              name="appearance-search-code"
              value={searchContent.code}
              onChange={(e) => setSearchContent({code: e.target.value})}
              required
            />
          </div>
          <div className="flex-fill w-100">
            <label htmlFor="appearance-search-name" className="form-label">
              現象名稱
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              id="searchCustomerCode"
              name="searchCustomerCode"
              value={searchContent.name}
              onChange={(e) => setSearchContent({name: e.target.value})}
              required
            />
          </div>
          <div className="flex-fill w-100">
            <label htmlFor="search-flag-status" className="form-label">
              狀態
            </label>
            <select
              className="form-select form-select-sm"
              id="search-flag-status"
              name="search-flag-status"
              value={searchContent.flgStatus}
              onChange={(e) => setSearchContent({flgStatus: e.target.value})}
              required
            >
              <option disabled value="">
                請選擇
              </option>
              <option value={undefined}>全部</option>
              <option value={enumList.caseFlgStatus.close}>未啟用</option>
              <option value={enumList.caseFlgStatus.open}>已啟用</option>
            </select>
          </div>
          <div>
            <button className="btn btn-primary" type="submit">
              <svg className="btn__icon" aria-hidden="true">
                <use href="#ico-search"></use>
              </svg>
            </button>
          </div>
        </form>
      </div>
      <div className="datatable__tool">
        <div className="d-flex align-items-center gap-2"></div>
        <div className="d-flex align-items-center gap-2">
          {baseComponent.checkPermission(
            enumList.moduleEnums.appearance,
            enumList.accessModeEnums.create,
          ) && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              id="modalElevatorBtnNew"
              onClick={handleCreateClick}
            >
              <svg className="btn__icon me-1" aria-hidden="true">
                <use href="#ico-plus-square"></use>
              </svg>
              新增
            </button>
          )}
        </div>
      </div>
      <div className="datatable__data">
        <DataTableComponent
          tableRef={tableRef}
          data={appearances}
          slots={slotConfigs}
          columns={columnConfigs}
          options={dataTableConfigs}
          className="display table table-hover align-middle nowrap"
        >
          <thead>
            <tr>
              <th>現象編號</th>
              <th>現象名稱</th>
              <th>現象說明</th>
              <th>現象狀態</th>
              <th></th>
            </tr>
          </thead>
        </DataTableComponent>
      </div>
    </section>
  );
};

export default ListPage;
